import { Tower } from './Tower';
import { Monster } from '../monsters/Monster';

export class ChainLightningTower extends Tower {
  constructor(scene: Phaser.Scene, x: number, y: number) {
    // damage: 25, attackSpeed: 1.2, range: 150 - lightning chains to 3 targets
    super(scene, x, y, 25, 1.2, 150);
    
    this.removeAll();
    this.createLightningVisuals();
  }

  protected override createVisuals(): void {
    // Create lightning tower base
    const base = this.scene.add.circle(0, 0, 20, 0x1a1a2e);
    this.add(base);
    
    // Create tesla coil
    const coil = this.scene.add.rectangle(0, -15, 8, 25, 0x0f3460);
    this.add(coil);
    
    // Add electricity effect
    const spark = this.scene.add.circle(0, -25, 4, 0x16537e);
    this.add(spark);
    
    // Add electric rings
    const ring1 = this.scene.add.circle(0, 0, 15, 0xffd60a, 0);
    ring1.setStrokeStyle(2, 0xffd60a);
    const ring2 = this.scene.add.circle(0, 0, 25, 0xffd60a, 0);
    ring2.setStrokeStyle(1, 0xffd60a);
    this.add([ring1, ring2]);
  }

  private createLightningVisuals(): void {
    this.createVisuals();
  }

  protected override fireProjectile(target: Monster, damage: number, isCrit: boolean): void {
    // Start chain lightning from tower to first target
    this.createLightningChain(this.x, this.y, target, damage, isCrit, 3);
  }

  private createLightningChain(
    fromX: number, 
    fromY: number, 
    target: Monster, 
    damage: number, 
    isCrit: boolean, 
    remainingChains: number
  ): void {
    // Create lightning bolt visual
    const lightning = this.scene.add.graphics();
    this.drawLightningBolt(lightning, fromX, fromY, target.x, target.y, isCrit);
    
    // Apply damage to current target
    target.takeDamage(damage);
    
    if (isCrit) {
      this.showCritEffect(target.x, target.y);
    }
    
    // Remove lightning after short duration
    this.scene.time.delayedCall(150, () => {
      lightning.destroy();
    });
    
    // Chain to next target if chains remaining
    if (remainingChains > 1) {
      const nextTarget = this.findNearestTarget(target, 80); // Chain range: 80px
      if (nextTarget) {
        // Chain with reduced damage
        this.scene.time.delayedCall(100, () => {
          this.createLightningChain(
            target.x, 
            target.y, 
            nextTarget, 
            damage * 0.8, // 20% damage reduction per chain
            false, 
            remainingChains - 1
          );
        });
      }
    }
  }

  private drawLightningBolt(
    graphics: Phaser.GameObjects.Graphics, 
    fromX: number, 
    fromY: number, 
    toX: number, 
    toY: number, 
    isCrit: boolean
  ): void {
    const segments = 8;
    const points: { x: number; y: number }[] = [];
    
    // Add start point
    points.push({ x: fromX, y: fromY });
    
    // Create jagged lightning path
    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      const baseX = fromX + (toX - fromX) * t;
      const baseY = fromY + (toY - fromY) * t;
      
      // Add random offset for lightning effect
      const offsetX = (Math.random() - 0.5) * 20;
      const offsetY = (Math.random() - 0.5) * 20;
      
      points.push({ 
        x: baseX + offsetX, 
        y: baseY + offsetY 
      });
    }
    
    // Add end point
    points.push({ x: toX, y: toY });
    
    // Draw lightning
    graphics.lineStyle(isCrit ? 4 : 2, isCrit ? 0xff0000 : 0xffd60a, 0.9);
    graphics.beginPath();
    graphics.moveTo(points[0]?.x ?? 0, points[0]?.y ?? 0);
    
    for (let i = 1; i < points.length; i++) {
      graphics.lineTo(points[i]?.x ?? 0, points[i]?.y ?? 0);
    }
    
    graphics.strokePath();
  }

  private findNearestTarget(currentTarget: Monster, maxDistance: number): Monster | null {
    if (!this.scene || !this.scene.children) return null;
    
    let nearestTarget: Monster | null = null;
    let nearestDistance = maxDistance;
    
    this.scene.children.list.forEach((child) => {
      if (child instanceof Monster && child !== currentTarget && child.getIsActive()) {
        const distance = Phaser.Math.Distance.Between(
          currentTarget.x, currentTarget.y, 
          child.x, child.y
        );
        
        if (distance <= maxDistance && distance < nearestDistance) {
          nearestDistance = distance;
          nearestTarget = child;
        }
      }
    });
    
    return nearestTarget;
  }
} 